import React, {useEffect, useRef, useState, useCallback} from 'react'
import ReactDOM from 'react-dom'
import {Box, Text, Button, IconButton, useMultiStyleConfig} from '@chakra-ui/react'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useHeaderContext} from '../../contexts/header-context'
import {CustomClose, CheckCircleWhite} from '../icons'

const TOAST_ICONS = {
    CheckCircleWhite
}

const DEFAULT_ICON = 'CheckCircleWhite'

const SingleToast = ({toast, onRemove, styles: themeStyles}) => {
    const {
        id,
        message,
        actionLabel,
        onActionClick,
        styles,
        messageStyles,
        returnFocusElement,
        focusActionOnMount,
        onManualClose,
        duration,
        pauseOnHover,
        icon
    } = toast

    const timerRef = useRef(null)
    const actionTriggerKeyRef = useRef(null)
    const actionButtonRef = useRef(null)
    const startedAtRef = useRef(0)
    const [remainingDuration, setRemainingDuration] = useState(duration)
    const [isTimerPaused, setIsTimerPaused] = useState(false)

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    useEffect(() => {
        setRemainingDuration(duration)
        setIsTimerPaused(false)
    }, [duration])

    useEffect(() => {
        if (!focusActionOnMount || !actionLabel || !actionButtonRef.current?.focus) return

        const focusAction = () => {
            try {
                actionButtonRef.current.focus({preventScroll: true})
            } catch (error) {
                actionButtonRef.current.focus()
            }
        }

        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(focusAction)
            return
        }

        focusAction()
    }, [actionLabel, focusActionOnMount])

    const restoreFocus = useCallback(() => {
        if (!returnFocusElement?.focus) return

        const focusTrigger = () => {
            try {
                returnFocusElement.focus({preventScroll: true})
            } catch (error) {
                returnFocusElement.focus()
            }
        }

        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(focusTrigger)
            return
        }

        focusTrigger()
    }, [returnFocusElement])

    useEffect(() => {
        if (isTimerPaused) {
            clearTimer()
            return
        }

        startedAtRef.current = Date.now()
        timerRef.current = setTimeout(() => {
            onRemove(id)
            timerRef.current = null
        }, remainingDuration)

        return clearTimer
    }, [isTimerPaused, remainingDuration, onRemove, id, clearTimer])

    const handleClose = (e) => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        clearTimer()
        onManualClose?.()
        onRemove(id)
    }

    const handleKeyDown = (event) => {
        if (event.key !== 'Escape') return

        handleClose(event)
        restoreFocus()
    }

    const handleMouseEnter = () => {
        if (!pauseOnHover || isTimerPaused) return
        const elapsed = Date.now() - startedAtRef.current
        setRemainingDuration((current) => Math.max(0, current - elapsed))
        setIsTimerPaused(true)
    }

    const handleMouseLeave = () => {
        if (!pauseOnHover) return
        setIsTimerPaused(false)
    }

    const iconName = icon === false ? null : (icon || DEFAULT_ICON)
    const IconComponent = iconName ? TOAST_ICONS[iconName] : null

    const handleActionKeyDown = (event) => {
        actionTriggerKeyRef.current = event.key
    }

    const handleActionClick = (event) => {
        onActionClick?.(event, {
            triggerKey: actionTriggerKeyRef.current
        })
        actionTriggerKeyRef.current = null
    }

    return (
        <Box
            data-testid="custom-toast"
            sx={themeStyles.toast}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
            {...styles}
        >
            {IconComponent && (
                <Box sx={themeStyles.icon}>
                    <IconComponent boxSize={themeStyles.icon.boxSize} color={themeStyles.icon.color} />
                </Box>
            )}
            <Box
                sx={{
                    ...themeStyles.message,
                    ...messageStyles
                }}
            >
                {message}
            </Box>
            {actionLabel && (
                <Button
                    ref={actionButtonRef}
                    variant='unstyled'
                    onClick={handleActionClick}
                    onKeyDown={handleActionKeyDown}
                    {...themeStyles.action}
                >
                    <Text as='span' {...themeStyles.action}>
                        {actionLabel}
                    </Text>
                </Button>
            )}
            <IconButton
                icon={<CustomClose sx={themeStyles.closeIcon} />}
                onClick={handleClose}
                aria-label="Close"
                variant="ghost"
                size="sm"
                sx={themeStyles.closeButton}
            />
        </Box>
    )
}

const CustomToast = () => {
    const {toasts, removeToast} = useCustomToast()
    const {headerRef} = useHeaderContext()
    const [visibleHeaderBottom, setVisibleHeaderBottom] = useState(0)
    const rafRef = useRef(null)
    const themeStyles = useMultiStyleConfig('CustomToast')

    useEffect(() => {
        const update = () => {
            if (headerRef.current) {
                const bottom = Math.max(0, Math.round(headerRef.current.getBoundingClientRect().bottom))
                setVisibleHeaderBottom((prev) => (prev !== bottom ? bottom : prev))
            }
        }

        update()

        const onScroll = () => {
            if (rafRef.current) return
            rafRef.current = requestAnimationFrame(() => {
                update()
                rafRef.current = null
            })
        }

        window.addEventListener('scroll', onScroll, {passive: true})
        window.addEventListener('resize', onScroll, {passive: true})

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        }
    }, [headerRef])

    if (!toasts || toasts.length === 0) return null
    if (typeof document === 'undefined') return null

    // Group toasts by their target container
    const grouped = {}
    toasts.forEach((toast) => {
        const key = toast.containerId || toast.toastContainerRef || '__body__'
        if (!grouped[key]) grouped[key] = []
        grouped[key].push(toast)
    })

    return Object.entries(grouped).map(([key, groupToasts]) => {
        const first = groupToasts[0]
        let container = first.toastContainerRef
        if (!container && first.containerId) {
            container = document.getElementById(first.containerId)
        }
        if (!container) {
            container = document.body
        }
        if (!container) return null

        // Toasts with a containerId use relative positioning (inline with header)
        const isInline = !!(first.containerId || first.toastContainerRef)

        return ReactDOM.createPortal(
            <Box
                data-testid="custom-toast-container"
                sx={{
                    ...themeStyles.container,
                    ...(isInline ? themeStyles.containerInline : themeStyles.containerFixed),
                    ...(!isInline && {top: `${visibleHeaderBottom}px`})
                }}
            >
                {groupToasts.map((toast) => (
                    <SingleToast
                        key={toast.id}
                        toast={toast}
                        onRemove={removeToast}
                        styles={themeStyles}
                    />
                ))}
            </Box>,
            container
        )
    })
}

export default CustomToast
